import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { AnnouncementForm } from '@/features/announcements/AnnouncementForm';
import { useAnnouncements } from '@/queries/announcements';
import { useApiError } from '@/lib/useApiError';
import type { Announcement } from '@/schemas/admin/announcements';

export const Route = createFileRoute('/_authed/announcements')({
  component: AnnouncementsScreen,
});

function AnnouncementsScreen() {
  const { t } = useTranslation('announcements');
  const list = useAnnouncements();
  const resolveError = useApiError();
  const [creating, setCreating] = React.useState(false);

  const columns: readonly DataColumn<Announcement>[] = [
    {
      id: 'title',
      header: t('table.title'),
      cell: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{r.title_en ?? '—'}</span>
          {r.title_ar ? (
            <span dir="rtl" className="text-xs text-[color:var(--color-fg-muted)]">
              {r.title_ar}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      id: 'severity',
      header: t('table.severity'),
      cell: (r) => (
        <Badge tone={r.severity === 'critical' ? 'danger' : r.severity === 'warn' ? 'warn' : 'info'}>
          {t(`form.severities.${r.severity}`)}
        </Badge>
      ),
      sortBy: (r) => r.severity,
    },
    {
      id: 'dismissible',
      header: t('table.dismissible'),
      cell: (r) => <Badge tone="neutral">{r.dismissible ? '✓' : '—'}</Badge>,
    },
    {
      id: 'starts',
      header: t('table.starts'),
      cell: (r) => <DateTime value={r.starts_at} />,
      sortBy: (r) => (r.starts_at ? new Date(r.starts_at) : null),
    },
    {
      id: 'ends',
      header: t('table.ends'),
      cell: (r) => <DateTime value={r.ends_at} />,
      sortBy: (r) => (r.ends_at ? new Date(r.ends_at) : null),
    },
    {
      id: 'locales',
      header: t('table.locales'),
      cell: (r) => {
        const locales = r.locales ?? [];
        if (locales.length === 0)
          return <span className="text-xs text-[color:var(--color-fg-muted)]">{t('table.all')}</span>;
        return (
          <div className="flex gap-1">
            {locales.map((l) => (
              <Badge key={l} tone="neutral">
                {l.toUpperCase()}
              </Badge>
            ))}
          </div>
        );
      },
    },
  ];

  if (creating) {
    return (
      <div className="space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">{t('new')}</h1>
            <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
          </div>
          <Button variant="ghost" onClick={() => setCreating(false)}>
            <X className="size-4" aria-hidden />
            {t('issues.back', { defaultValue: 'Back' })}
          </Button>
        </header>
        <Card>
          <CardContent className="pt-6">
            <AnnouncementForm
              onSaved={() => setCreating(false)}
              onCancel={() => setCreating(false)}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" aria-hidden />
          {t('new')}
        </Button>
      </header>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('list.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable<Announcement>
            data={list.data ?? []}
            columns={columns}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{ title: t('list.empty'), body: t('list.emptyBody') }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
